import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import {format,parseISO,addDays,subDays,isBefore,startOfDay} from 'date-fns';
import {useNavigate,useLocation} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import CaseSheetPrint from '../components/CaseSheetPrint';

const {FiCalendar,FiClock,FiUser,FiEdit,FiFilter,FiFileText,FiChevronLeft,FiChevronRight,FiUserCheck,FiEye,FiExternalLink,FiClipboard,FiX,FiChevronsLeft,FiChevronsRight,FiFlag}=FiIcons;

const Appointments=()=> {
const [appointments,setAppointments]=useState([]);
const [allAppointments,setAllAppointments]=useState([]);
const [patients,setPatients]=useState([]);
const [doctors,setDoctors]=useState([]);
const [loading,setLoading]=useState(true);
const [selectedDate,setSelectedDate]=useState(format(new Date(),'yyyy-MM-dd'));
const [showModal,setShowModal]=useState(false);
const [showViewModal,setShowViewModal]=useState(false);
const [showInvoiceModal,setShowInvoiceModal]=useState(false);
const [showCaseSheetModal,setShowCaseSheetModal]=useState(false);
const [showCancelModal,setShowCancelModal]=useState(false);
const [selectedAppointment,setSelectedAppointment]=useState(null);
const [selectedInvoice,setSelectedInvoice]=useState(null);
const [formData,setFormData]=useState({
patient_id: '',
doctor_id: '',
appointment_date: format(new Date(),'yyyy-MM-dd'),
start_time: '09:00',
end_time: '09:30',
notes: ''
});
const [showFilters,setShowFilters]=useState(false);
const [filters,setFilters]=useState({
uninvoiced: false,
patient_id: '',
allDates: false,
include_flagged: false
});
const [cancelReason,setCancelReason]=useState('');
// Pagination state
const [pagination,setPagination]=useState({
currentPage: 1,
totalPages: 1,
itemsPerPage: 10,
totalItems: 0
});
// FIXED: Add state to store the doctor's record ID for filtering
const [doctorRecordId,setDoctorRecordId]=useState(null);

const navigate=useNavigate();
const location=useLocation();
const {user}=useAuth();

const queryParams=new URLSearchParams(location.search);

// Format Patient ID with leading zeroes
const formatPatientId = (id) => {
  return String(id).padStart(6, '0');
};

// First effect: Initialize filters from URL params and handle new appointments
useEffect(()=> {
// Check URL params for patient_id filter
const patientId=queryParams.get('patient_id');
if (patientId) {
console.log(`Setting patient filter from URL: ${patientId}`);
const timeoutId=setTimeout(()=> {
setFilters(prev=> ({
...prev,
patient_id: patientId
}));
},50);
}

// Check for new appointment with prefilled patient
const isNew=queryParams.get('new');
if (isNew==='true' && patientId) {
setFormData(prev=> ({
...prev,
patient_id: patientId
}));
setShowModal(true);
}

fetchPatients();
fetchDoctors();
},[location.search]);

// FIXED: New effect to get doctor record ID if user is a doctor
useEffect(()=> {
const fetchDoctorRecordId=async ()=> {
if (user.role==='doctor') {
try {
console.log('🔍 Fetching doctor record for user:', user.id, user.name);
const response=await axios.get('/api/doctors');
const doctorRecord=response.data.find(d=> d.user_id===user.id);
if (doctorRecord) {
console.log('✅ Found doctor record:', doctorRecord.id, 'for user:', user.id);
setDoctorRecordId(doctorRecord.id);
} else {
console.log('❌ No doctor record found for user:', user.id);
setDoctorRecordId(null);
}
} catch (error) {
console.error('Error fetching doctor record:', error);
setDoctorRecordId(null);
}
}
};

fetchDoctorRecordId();
},[user]);

// Second effect: Fetch appointments when filters change or doctor record is found
useEffect(()=> {
console.log("Fetching appointments with filters:",{
selectedDate,
uninvoiced: filters.uninvoiced,
patient_id: filters.patient_id,
allDates: filters.allDates,
include_flagged: filters.include_flagged,
doctorRecordId: doctorRecordId
});
fetchAppointments();
},[selectedDate,filters,doctorRecordId]);

// Third effect: Update displayed appointments when pagination or all appointments change
useEffect(()=> {
updatePaginatedData();
},[pagination.currentPage,allAppointments]);

const fetchAppointments=async ()=> {
try {
setLoading(true);

// Build query parameters
const params=new URLSearchParams();

// Only apply date filter if not viewing all dates for a patient
if (!filters.allDates) {
params.append('date',selectedDate);
}

if (filters.patient_id) {
params.append('patient_id',filters.patient_id);
}

if (filters.uninvoiced) {
params.append('uninvoiced','true');
}

// Only include flagged appointments if user is admin AND specifically requests them
if (user?.role === 'admin' && filters.include_flagged) {
params.append('include_flagged', 'true');
}

console.log(`Fetching appointments with URL params: ${params.toString()}`);

// OPTIMIZED: Single query that gets all appointment data including invoice status
const response=await axios.get(`/api/appointments?${params.toString()}`);
console.log(`Received ${response.data.length} appointments from server`);

// The server now returns appointments with hasActiveInvoice and invoiceId already populated
// No need for additional API calls per appointment
const appointmentsWithInvoiceStatus=response.data;

// Update allAppointments state
setAllAppointments(appointmentsWithInvoiceStatus);

// Calculate total pages
const totalItems=appointmentsWithInvoiceStatus.length;
const totalPages=Math.max(1,Math.ceil(totalItems / pagination.itemsPerPage));

// Update pagination info
setPagination(prev=> ({
...prev,
totalPages,
totalItems,
currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
}));

} catch (error) {
console.error('Error fetching appointments:',error);
// On error,clear appointments
setAllAppointments([]);
setPagination(prev=> ({
...prev,
totalPages: 1,
totalItems: 0,
currentPage: 1
}));
} finally {
setLoading(false);
}
};

const updatePaginatedData=()=> {
const startIndex=(pagination.currentPage - 1) * pagination.itemsPerPage;
const endIndex=startIndex + pagination.itemsPerPage;

// Get appointments for current page
const paginatedAppointments=allAppointments.slice(startIndex,endIndex);
setAppointments(paginatedAppointments);
};

const handlePageChange=(newPage)=> {
setPagination(prev=> ({
...prev,
currentPage: newPage
}));

// Scroll to top when page changes
window.scrollTo({top: 0,behavior: 'smooth'});
};

const fetchPatients=async ()=> {
try {
const response=await axios.get('/api/patients');
setPatients(response.data);
} catch (error) {
console.error('Error fetching patients:',error);
}
};

const fetchDoctors=async ()=> {
try {
// Only fetch active doctors for appointment creation
const response=await axios.get('/api/doctors');
setDoctors(response.data);
} catch (error) {
console.error('Error fetching doctors:',error);
}
};

const handleSubmit=async (e)=> {
e.preventDefault();
try {
// Combine date and time fields
const datetime_start=`${formData.appointment_date}T${formData.start_time}`;
const datetime_end=`${formData.appointment_date}T${formData.end_time}`;

// REMOVED: Past date validation - now allows appointments for any date
// const appointmentDate=new Date(formData.appointment_date);
// const today=startOfDay(new Date());
// if (isBefore(appointmentDate,today)) {
// alert('Cannot create appointments for past dates.');
// return;
//}

const appointmentData={
patient_id: formData.patient_id,
doctor_id: formData.doctor_id,
datetime_start,
datetime_end,
notes: formData.notes
};

if (selectedAppointment) {
// Check if appointment is completed or flagged - if so,prevent edit
if (selectedAppointment.status==='completed' || selectedAppointment.status==='flagged') {
alert('Completed or flagged appointments cannot be edited.');
setShowModal(false);
return;
}
await axios.put(`/api/appointments/${selectedAppointment.id}`,appointmentData);
} else {
await axios.post('/api/appointments',appointmentData);
}

setShowModal(false);
setSelectedAppointment(null);
setFormData({
patient_id: '',
doctor_id: '',
appointment_date: format(new Date(),'yyyy-MM-dd'),
start_time: '09:00',
end_time: '09:30',
notes: ''
});
fetchAppointments();

// Clean up URL if it was used for creating appointment
if (queryParams.has('new')) {
navigate('/appointments',{replace: true});
}
} catch (error) {
console.error('Error saving appointment:',error);
alert(`Error: ${error.response?.data?.error || 'Failed to save appointment'}`);
}
};

const openModal=(appointment=null)=> {
// If appointment is completed or flagged,don't allow editing
if (appointment && (appointment.status==='completed' || appointment.status==='flagged')) {
alert('Completed or flagged appointments cannot be edited.');
return;
}

setSelectedAppointment(appointment);
if (appointment) {
const appointmentDate=format(parseISO(appointment.datetime_start),'yyyy-MM-dd');
const startTime=format(parseISO(appointment.datetime_start),'HH:mm');
const endTime=format(parseISO(appointment.datetime_end),'HH:mm');

setFormData({
patient_id: appointment.patient_id,
doctor_id: appointment.doctor_id,
appointment_date: appointmentDate,
start_time: startTime,
end_time: endTime,
notes: appointment.notes || ''
});
} else {
// REMOVED: Minimum date restriction - now allows any date selection
// const today=format(new Date(),'yyyy-MM-dd');
// Keep patient_id if it was set from URL params
setFormData(prev=> ({
...prev,
doctor_id: '',
appointment_date: format(new Date(),'yyyy-MM-dd'),// Default to today but allow any date
start_time: '09:00',
end_time: '09:30',
notes: ''
}));
}
setShowModal(true);
};

const openViewModal=async (appointment)=> {
try {
const response=await axios.get(`/api/appointments/${appointment.id}`);
setSelectedAppointment(response.data);
setShowViewModal(true);
} catch (error) {
console.error('Error fetching appointment details:',error);
}
};

const openInvoiceModal=async (invoiceId)=> {
try {
const response=await axios.get(`/api/invoices/${invoiceId}`);
setSelectedInvoice(response.data);
setShowInvoiceModal(true);
} catch (error) {
console.error('Error fetching invoice details:',error);
alert('Error loading invoice details');
}
};

const openCaseSheetModal=async (appointment)=> {
try {
// Get full patient details for the appointment
const patientResponse=await axios.get(`/api/patients/${appointment.patient_id}`);
const appointmentWithPatientDetails={
...appointment,
patient_dob: patientResponse.data.date_of_birth,
patient_sex: patientResponse.data.sex,
patient_national_id: patientResponse.data.national_id,
nationality: patientResponse.data.nationality,
contact_number: patientResponse.data.contact_number
};
setSelectedAppointment(appointmentWithPatientDetails);
setShowCaseSheetModal(true);
} catch (error) {
console.error('Error preparing case sheet:',error);
alert('Error loading patient details for case sheet');
}
};

const openCancelModal=(appointment)=> {
// Only allow cancellation of scheduled appointments
if (appointment.status !=='scheduled') {
alert('Only scheduled appointments can be cancelled.');
return;
}

setSelectedAppointment(appointment);
setCancelReason('');
setShowCancelModal(true);
};

const handleCancelAppointment=async ()=> {
try {
await axios.put(`/api/appointments/${selectedAppointment.id}/status`,{
status: 'cancelled'
});
setShowCancelModal(false);
fetchAppointments();
} catch (error) {
console.error('Error cancelling appointment:',error);
alert('Failed to cancel appointment');
}
};

const getStatusColor=(status)=> {
switch (status) {
case 'scheduled': return 'bg-blue-100 text-blue-800';
case 'completed': return 'bg-green-100 text-green-800';
case 'cancelled': return 'bg-red-100 text-red-800';
case 'flagged': return 'bg-purple-100 text-purple-800';
default: return 'bg-grey-100 text-grey-800';
}
};

const getInvoiceStatusColor=(status)=> {
switch (status) {
case 'paid': return 'bg-green-100 text-green-800';
case 'partial': return 'bg-yellow-100 text-yellow-800';
case 'unpaid': return 'bg-red-100 text-red-800';
case 'cancelled': return 'bg-grey-100 text-grey-800';
default: return 'bg-grey-100 text-grey-800';
}
};

const createInvoice=(appointment)=> {
if (appointment.hasActiveInvoice) {
alert('This appointment already has an active invoice.');
return;
}
navigate(`/invoices?new=true&appointment_id=${appointment.id}&patient_id=${appointment.patient_id}`);
};

const clearFilters=()=> {
setFilters({
uninvoiced: false,
patient_id: '',
allDates: false,
include_flagged: false
});
setSelectedDate(format(new Date(),'yyyy-MM-dd'));
navigate('/appointments',{replace: true});
};

const toggleAllDatesFilter=()=> {
setFilters(prev=> ({
...prev,
allDates: !prev.allDates
}));
setPagination(prev=> ({
...prev,
currentPage: 1
}));
};

const handleStartTimeChange=(e)=> {
const newStartTime=e.target.value;
setFormData(prev=> {
// Calculate a default end time 30 minutes after start time
let endTime=prev.end_time;
const startHours=parseInt(newStartTime.split(':')[0]);
const startMinutes=parseInt(newStartTime.split(':')[1]);

// Create a date object with the start time
const startDate=new Date();
startDate.setHours(startHours);
startDate.setMinutes(startMinutes);

// Add 30 minutes
const endDate=new Date(startDate.getTime() + 30 * 60000);
const newEndTime=`${String(endDate.getHours()).padStart(2,'0')}:${String(endDate.getMinutes()).padStart(2,'0')}`;

// If the current end time is earlier than the new start time,update it
if (prev.end_time <=newStartTime) {
endTime=newEndTime;
}

return {
...prev,
start_time: newStartTime,
end_time: endTime
};
});
};

const goToPreviousDay=()=> {
const currentDate=new Date(selectedDate);
const previousDay=subDays(currentDate,1);
setSelectedDate(format(previousDay,'yyyy-MM-dd'));
};

const goToNextDay=()=> {
const currentDate=new Date(selectedDate);
const nextDay=addDays(currentDate,1);
setSelectedDate(format(nextDay,'yyyy-MM-dd'));
};

const markPatientRegistered=async (appointmentId)=> {
try {
await axios.put(`/api/appointments/${appointmentId}/status`,{
status: 'completed'
});
fetchAppointments();
} catch (error) {
console.error('Error updating appointment status:',error);
alert(`Error: ${error.response?.data?.error || 'Failed to update appointment status'}`);
}
};

// FIXED: Filter appointments based on user role using the correct doctor record ID
const filteredAppointments=appointments.filter(appointment=> {
// If user is a doctor,only show their own appointments using doctor record ID
if (user.role==='doctor') {
console.log('🔍 Filtering appointments for doctor:', {
userRole: user.role,
userId: user.id,
doctorRecordId: doctorRecordId,
appointmentDoctorId: appointment.doctor_id,
match: appointment.doctor_id===doctorRecordId
});
return appointment.doctor_id===doctorRecordId;
}
// Otherwise show all appointments
return true;
});

console.log('📊 Appointment filtering results:', {
totalAppointments: appointments.length,
filteredAppointments: filteredAppointments.length,
userRole: user.role,
doctorRecordId: doctorRecordId
});

// Get the selected patient's name for display
const selectedPatient=patients.find(p=> p.id==filters.patient_id);

if (loading) {
return (
<div className="flex items-center justify-center h-64">
<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-maroon-600"></div>
</div>
);
}

return (
<div className="space-y-6">
<div className="flex justify-between items-center">
<h1 className="text-2xl font-bold text-grey-900">
Appointments
{selectedPatient && (
<span className="ml-2 text-lg font-normal text-grey-600">
for {selectedPatient.full_name}
</span>
)}
{user.role==='doctor' && (
<span className="ml-2 text-lg font-normal text-grey-600">
for Dr. {user.name}
</span>
)}
</h1>
</div>

<div className="bg-white shadow rounded-lg">
<div className="p-6 border-b border-grey-200">
<div className="flex flex-col md:flex-row md:items-center gap-4">
<div className="flex items-center space-x-4">
{!filters.allDates && (
<>
<button
onClick={goToPreviousDay}
className="p-2 rounded-md text-grey-600 hover:text-maroon-600 hover:bg-grey-100"
title="Previous Day"
>
<SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
</button>
<div className="flex items-center space-x-2">
<SafeIcon icon={FiCalendar} className="w-5 h-5 text-grey-400" />
<input
type="date"
value={selectedDate}
onChange={(e)=> setSelectedDate(e.target.value)}
className="border border-grey-300 rounded-lg px-3 py-2 focus:ring-maroon-500 focus:border-maroon-500"
/>
</div>
<button
onClick={goToNextDay}
className="p-2 rounded-md text-grey-600 hover:text-maroon-600 hover:bg-grey-100"
title="Next Day"
>
<SafeIcon icon={FiChevronRight} className="w-5 h-5" />
</button>
</>
)}

{filters.patient_id && (
<button
onClick={toggleAllDatesFilter}
className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
filters.allDates 
? 'bg-blue-600 text-white' 
: 'bg-blue-100 text-blue-800 hover:bg-blue-200'
}`}
>
{filters.allDates ? 'Show Selected Date Only' : 'Show All Appointments'}
</button>
)}

<span className="text-sm text-grey-600">
{pagination.totalItems} appointment{pagination.totalItems !==1 ? 's' : ''} 
{!filters.allDates ? ' for selected date' : ' found'}
</span>
</div>

<div className="flex items-center space-x-2 ml-auto">
{selectedPatient && (
<span className="text-sm font-medium text-grey-700 bg-blue-50 px-3 py-1 rounded-full">
{selectedPatient.full_name}
</span>
)}

<button
onClick={()=> setShowFilters(!showFilters)}
className="bg-grey-200 hover:bg-grey-300 text-grey-800 px-4 py-2 rounded-lg transition-colors flex items-center space-x-1"
>
<SafeIcon icon={FiFilter} className="w-4 h-4" />
<span>Filters</span>
</button>

{(filters.uninvoiced || filters.patient_id || filters.include_flagged) && (
<button
onClick={clearFilters}
className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-lg transition-colors text-sm"
>
Clear Filters
</button>
)}
</div>
</div>

{showFilters && (
<div className="mt-4 p-4 bg-grey-50 rounded-lg">
<h3 className="text-sm font-medium text-grey-700 mb-2">Filters</h3>
<div className="flex flex-wrap gap-4">
<label className="flex items-center space-x-2">
<input
type="checkbox"
className="rounded border-grey-300 text-maroon-600 focus:ring-maroon-500"
checked={filters.uninvoiced}
onChange={(e)=> setFilters({...filters,uninvoiced: e.target.checked})}
/>
<span className="text-sm text-grey-700">Uninvoiced Appointments</span>
</label>

{/* Only show flagged filter for admin users and keep it disabled by default */}
{user?.role === 'admin' && (
<label className="flex items-center space-x-2">
<input
type="checkbox"
className="rounded border-grey-300 text-purple-600 focus:ring-purple-500"
checked={filters.include_flagged}
onChange={(e)=> setFilters({...filters,include_flagged: e.target.checked})}
/>
<span className="text-sm text-grey-700">Include flagged appointments</span>
</label>
)}

{/* Only show patient filter if user is not a doctor */}
{user.role !=='doctor' && (
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Patient
</label>
<select
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={filters.patient_id}
onChange={(e)=> setFilters({...filters,patient_id: e.target.value})}
>
<option value="">All Patients</option>
{patients.map((patient)=> (
<option key={patient.id} value={patient.id}>
{patient.full_name}
</option>
))}
</select>
</div>
)}
</div>
</div>
)}
</div>

<div className="divide-y divide-grey-200">
{filteredAppointments.length===0 ? (
<div className="p-12 text-center">
<SafeIcon icon={FiCalendar} className="w-12 h-12 text-grey-400 mx-auto mb-4" />
<h3 className="text-lg font-medium text-grey-900 mb-2">No appointments found</h3>
<p className="text-grey-600 mb-4">
{filters.patient_id 
? filters.allDates 
? "No appointments scheduled for this patient."
: "No appointments scheduled for this patient on the selected date."
: user.role==='doctor'
? "No appointments scheduled for you on the selected date."
: "Schedule appointments from the patient page."
}
</p>
{queryParams.has('patient_id') && ['admin','manager','receptionist'].includes(user.role) && (
<button
onClick={()=> openModal()}
className="bg-maroon-600 hover:bg-maroon-700 text-white px-4 py-2 rounded-lg"
>
Schedule Appointment
</button>
)}
</div>
) : (
filteredAppointments.map((appointment)=> (
<motion.div
key={appointment.id}
initial={{opacity: 0}}
animate={{opacity: 1}}
className="p-6 hover:bg-grey-50 transition-colors"
>
<div className="flex items-center justify-between">
<div className="flex items-center space-x-4">
<div className="flex-shrink-0">
<div className="w-10 h-10 bg-maroon-100 rounded-full flex items-center justify-center">
<SafeIcon icon={FiUser} className="w-5 h-5 text-maroon-600" />
</div>
</div>
<div>
<h3 className="text-lg font-medium text-grey-900">
{appointment.patient_name}
</h3>
<div className="flex items-center space-x-4 text-sm text-grey-600">
<div className="flex items-center space-x-1">
<span className="font-medium">Patient ID:</span>
<span>#{formatPatientId(appointment.patient_id)}</span>
</div>
<div className="flex items-center space-x-1">
<SafeIcon icon={FiCalendar} className="w-4 h-4" />
<span>
{format(parseISO(appointment.datetime_start),'dd/MM/yyyy')}
</span>
</div>
<div className="flex items-center space-x-1">
<SafeIcon icon={FiClock} className="w-4 h-4" />
<span>
{format(parseISO(appointment.datetime_start),'HH:mm')} - 
{format(parseISO(appointment.datetime_end),'HH:mm')}
</span>
</div>
<div className="flex items-center space-x-1">
<SafeIcon icon={FiUser} className="w-4 h-4" />
<span>{appointment.doctor_name}</span>
</div>
</div>
{appointment.notes && (
<p className="text-sm text-grey-600 mt-1">{appointment.notes}</p>
)}
{appointment.status==='completed' && !appointment.hasActiveInvoice && (
<div className="mt-1 text-xs text-orange-600">Ready for invoicing</div>
)}
{appointment.hasActiveInvoice && (
<button
onClick={()=> openInvoiceModal(appointment.invoiceId)}
className="mt-1 text-xs text-green-600 hover:text-green-800 hover:underline flex items-center space-x-1 transition-colors"
>
<span>Invoice created</span>
<SafeIcon icon={FiExternalLink} className="w-3 h-3" />
</button>
)}
</div>
</div>

<div className="flex flex-col items-end space-y-2">
<div className="flex items-center space-x-2">
<span
className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
appointment.status
)}`}
>
{appointment.status}
</span>
{appointment.status === 'flagged' && (
<SafeIcon icon={FiFlag} className="w-4 h-4 text-purple-600" title="Flagged for admin review" />
)}
</div>

<div className="flex space-x-2">
{/* View button - always shown */}
<button
onClick={()=> openViewModal(appointment)}
className="text-blue-600 hover:text-blue-900"
title="View Details"
>
<SafeIcon icon={FiEye} className="w-4 h-4" />
</button>

{/* Only show edit button if appointment is not completed or flagged */}
{appointment.status !=='completed' && appointment.status !=='flagged' && ['admin','manager','receptionist'].includes(user.role) && (
<button
onClick={()=> openModal(appointment)}
className="text-maroon-600 hover:text-maroon-900"
title="Edit Appointment"
>
<SafeIcon icon={FiEdit} className="w-4 h-4" />
</button>
)}

{/* Add Cancel button for scheduled appointments */}
{appointment.status==='scheduled' && ['admin','manager','receptionist'].includes(user.role) && (
<button
onClick={()=> openCancelModal(appointment)}
className="text-red-600 hover:text-red-900"
title="Cancel Appointment"
>
<SafeIcon icon={FiX} className="w-4 h-4" />
</button>
)}

{appointment.status==='scheduled' && ['admin','manager','receptionist','doctor'].includes(user.role) && (
<button
onClick={()=> markPatientRegistered(appointment.id)}
className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
title="Mark as Registered"
>
<SafeIcon icon={FiUserCheck} className="w-4 h-4" />
<span className="text-xs">Register</span>
</button>
)}

{/* Case Sheet button - only show for completed appointments with no invoice */}
{appointment.status==='completed' && !appointment.hasActiveInvoice && (
<button
onClick={()=> openCaseSheetModal(appointment)}
className="text-purple-600 hover:text-purple-900 flex items-center space-x-1"
title="Print Case Sheet"
>
<SafeIcon icon={FiClipboard} className="w-4 h-4" />
<span className="text-xs">Case Sheet</span>
</button>
)}

{appointment.status==='completed' && !appointment.hasActiveInvoice && ['admin','manager','receptionist'].includes(user.role) && (
<button
onClick={()=> createInvoice(appointment)}
className="text-green-600 hover:text-green-900 flex items-center space-x-1"
title="Create Invoice"
>
<SafeIcon icon={FiFileText} className="w-4 h-4" />
<span className="text-xs">Create Invoice</span>
</button>
)}
</div>
</div>
</div>
</motion.div>
))
)}
</div>

{/* Pagination Controls */}
{pagination.totalPages > 1 && (
<div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex flex-wrap items-center justify-between">
<div className="text-sm text-grey-700">
Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
{Math.min(pagination.currentPage * pagination.itemsPerPage,pagination.totalItems)} of{' '}
{pagination.totalItems} appointments
</div>
<div className="flex space-x-2 mt-2 sm:mt-0">
<button
onClick={()=> handlePageChange(1)}
disabled={pagination.currentPage===1}
className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
title="First Page"
>
<SafeIcon icon={FiChevronsLeft} className="w-5 h-5" />
</button>
<button
onClick={()=> handlePageChange(pagination.currentPage - 1)}
disabled={pagination.currentPage===1}
className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
title="Previous Page"
>
<SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
</button>
<div className="flex items-center px-4">
<span className="text-grey-700">Page {pagination.currentPage} of {pagination.totalPages}</span>
</div>
<button
onClick={()=> handlePageChange(pagination.currentPage + 1)}
disabled={pagination.currentPage===pagination.totalPages}
className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
title="Next Page"
>
<SafeIcon icon={FiChevronRight} className="w-5 h-5" />
</button>
<button
onClick={()=> handlePageChange(pagination.totalPages)}
disabled={pagination.currentPage===pagination.totalPages}
className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
title="Last Page"
>
<SafeIcon icon={FiChevronsRight} className="w-5 h-5" />
</button>
</div>
</div>
)}
</div>

{/* Edit Modal */}
{showModal && (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
<motion.div
initial={{scale: 0.95,opacity: 0}}
animate={{scale: 1,opacity: 1}}
className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto"
>
<h3 className="text-lg font-medium text-grey-900 mb-4">
{selectedAppointment ? 'Edit Appointment' : 'New Appointment'}
</h3>

<form onSubmit={handleSubmit} className="space-y-4">
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Patient
</label>
<select
required
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={formData.patient_id}
onChange={(e)=> setFormData({...formData,patient_id: e.target.value})}
disabled={queryParams.has('patient_id')}
>
<option value="">Select Patient</option>
{patients.map((patient)=> (
<option key={patient.id} value={patient.id}>
{patient.full_name}
</option>
))}
</select>
</div>

<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Doctor
</label>
<select
required
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={formData.doctor_id}
onChange={(e)=> setFormData({...formData,doctor_id: e.target.value})}
>
<option value="">Select Doctor</option>
{doctors
.filter(doctor=> doctor.active) // Only show active doctors
.map((doctor)=> (
<option key={doctor.id} value={doctor.id}>
{doctor.name} {!doctor.active && ' (Inactive)'}
</option>
))}
</select>
</div>

<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Appointment Date
</label>
<input
type="date"
required
// REMOVED: min date restriction - now allows any date
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={formData.appointment_date}
onChange={(e)=> setFormData({...formData,appointment_date: e.target.value})}
/>
</div>

<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Start Time
</label>
<input
type="time"
required
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={formData.start_time}
onChange={handleStartTimeChange}
/>
</div>
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
End Time
</label>
<input
type="time"
required
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={formData.end_time}
min={formData.start_time}
onChange={(e)=> setFormData({...formData,end_time: e.target.value})}
/>
</div>
</div>

<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Notes
</label>
<textarea
rows={3}
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={formData.notes}
onChange={(e)=> setFormData({...formData,notes: e.target.value})}
/>
</div>

<div className="flex space-x-3 pt-4">
<button
type="button"
onClick={()=> {
setShowModal(false);
// Clean up URL if it was used for creating appointment
if (queryParams.has('new')) {
navigate('/appointments',{replace: true});
}
}}
className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
>
Cancel
</button>
<button
type="submit"
className="flex-1 px-4 py-2 bg-maroon-600 text-white rounded-lg hover:bg-maroon-700 transition-colors"
>
{selectedAppointment ? 'Update' : 'Create'}
</button>
</div>
</form>
</motion.div>
</div>
)}

{/* Cancel Appointment Modal */}
{showCancelModal && selectedAppointment && (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
<motion.div
initial={{scale: 0.95,opacity: 0}}
animate={{scale: 1,opacity: 1}}
className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
>
<h3 className="text-lg font-medium text-grey-900 mb-4">Cancel Appointment</h3>
<p className="text-sm text-grey-600 mb-4">
Are you sure you want to cancel this appointment for {selectedAppointment.patient_name}?
</p>
<div className="mb-4">
<label className="block text-sm font-medium text-grey-700 mb-1">
Cancellation Reason (Optional)
</label>
<textarea
rows={3}
className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
value={cancelReason}
onChange={(e)=> setCancelReason(e.target.value)}
placeholder="Enter reason for cancellation..."
/>
</div>
<div className="flex space-x-3">
<button
onClick={()=> setShowCancelModal(false)}
className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
>
Keep Appointment
</button>
<button
onClick={handleCancelAppointment}
className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
>
Cancel Appointment
</button>
</div>
</motion.div>
</div>
)}

{/* View Appointment Modal */}
{showViewModal && selectedAppointment && (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
<motion.div
initial={{scale: 0.95,opacity: 0}}
animate={{scale: 1,opacity: 1}}
className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto"
>
<h3 className="text-lg font-medium text-grey-900 mb-4">
Appointment Details
</h3>

<div className="space-y-4">
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Patient
</label>
<p className="text-sm text-grey-900">{selectedAppointment.patient_name}</p>
</div>

<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Patient ID
</label>
<p className="text-sm text-grey-900">#{formatPatientId(selectedAppointment.patient_id)}</p>
</div>

<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Doctor
</label>
<p className="text-sm text-grey-900">{selectedAppointment.doctor_name}</p>
</div>

<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Date & Time
</label>
<p className="text-sm text-grey-900">
{format(parseISO(selectedAppointment.datetime_start),'MMM dd,yyyy')} at{' '}
{format(parseISO(selectedAppointment.datetime_start),'HH:mm')} -{' '}
{format(parseISO(selectedAppointment.datetime_end),'HH:mm')}
</p>
</div>

<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Status
</label>
<div className="flex items-center space-x-2">
<span
className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
selectedAppointment.status
)}`}
>
{selectedAppointment.status}
</span>
{selectedAppointment.status === 'flagged' && (
<SafeIcon icon={FiFlag} className="w-4 h-4 text-purple-600" title="Flagged for admin review" />
)}
</div>
</div>

{selectedAppointment.notes && (
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Notes
</label>
<p className="text-sm text-grey-900">{selectedAppointment.notes}</p>
</div>
)}

{selectedAppointment.treatments && selectedAppointment.treatments.length > 0 && (
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Treatments
</label>
<div className="space-y-2">
{selectedAppointment.treatments.map((treatment,index)=> (
<div key={index} className="bg-grey-50 p-3 rounded-lg">
<p className="text-sm font-medium text-grey-900">{treatment.description}</p>
{treatment.treatment_code && (
<p className="text-xs text-grey-500">Code: {treatment.treatment_code}</p>
)}
{treatment.cost && (
<p className="text-xs text-grey-500">Cost: QR {treatment.cost}</p>
)}
</div>
))}
</div>
</div>
)}
</div>

<div className="flex justify-end pt-4">
<button
onClick={()=> setShowViewModal(false)}
className="px-4 py-2 bg-grey-600 text-white rounded-lg hover:bg-grey-700 transition-colors"
>
Close
</button>
</div>
</motion.div>
</div>
)}

{/* View Invoice Modal */}
{showInvoiceModal && selectedInvoice && (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
<motion.div
initial={{scale: 0.95,opacity: 0}}
animate={{scale: 1,opacity: 1}}
className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto"
>
<div className="flex items-center justify-between mb-4">
<h3 className="text-lg font-medium text-grey-900">
Invoice #{selectedInvoice.id}
</h3>
<span
className={`px-3 py-1 rounded-full text-sm font-medium ${getInvoiceStatusColor(
selectedInvoice.status
)}`}
>
{selectedInvoice.status}
</span>
</div>

<div className="space-y-4">
<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Patient
</label>
<p className="text-sm text-grey-900">{selectedInvoice.patient_name}</p>
</div>
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Doctor
</label>
<p className="text-sm text-grey-900">{selectedInvoice.doctor_name || 'N/A'}</p>
</div>
</div>

<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Date
</label>
<p className="text-sm text-grey-900">
{format(parseISO(selectedInvoice.created_at),'MMM dd,yyyy')}
</p>
</div>
<div>
<label className="block text-sm font-medium text-grey-700 mb-1">
Payment Method
</label>
<p className="text-sm text-grey-900 capitalize">{selectedInvoice.payment_method}</p>
</div>
</div>

{selectedInvoice.treatments && selectedInvoice.treatments.length > 0 && (
<div>
<label className="block text-sm font-medium text-grey-700 mb-2">
Treatments
</label>
<div className="space-y-2">
{selectedInvoice.treatments.map((treatment,index)=> (
<div key={index} className="bg-grey-50 p-3 rounded-lg">
<div className="flex justify-between items-start">
<div>
<p className="text-sm font-medium text-grey-900">{treatment.description}</p>
{treatment.treatment_code && (
<p className="text-xs text-grey-500">Code: {treatment.treatment_code}</p>
)}
{treatment.is_specialist && (
<p className="text-xs text-blue-600">Specialist Treatment</p>
)}
</div>
<p className="text-sm font-medium text-grey-900">QR {treatment.cost}</p>
</div>
</div>
))}
</div>
</div>
)}

<div className="bg-blue-50 p-4 rounded-lg">
<div className="grid grid-cols-3 gap-4 text-sm">
<div>
<p className="text-blue-700">Total Amount</p>
<p className="font-medium text-blue-900">QR {selectedInvoice.total_amount}</p>
</div>
<div>
<p className="text-blue-700">Paid Amount</p>
<p className="font-medium text-blue-900">QR {selectedInvoice.paid_amount}</p>
</div>
<div>
<p className="text-blue-700">Outstanding</p>
<p className="font-medium text-blue-900">
QR {selectedInvoice.total_amount - selectedInvoice.paid_amount}
</p>
</div>
</div>
</div>

{selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
<div>
<label className="block text-sm font-medium text-grey-700 mb-2">
Payment History
</label>
<div className="space-y-2">
{selectedInvoice.payments.map((payment,index)=> (
<div key={index} className="bg-green-50 p-3 rounded-lg">
<div className="flex justify-between items-start">
<div>
<p className="text-sm font-medium text-green-900">
QR {payment.amount} - {payment.payment_method}
</p>
<p className="text-xs text-green-700">
{format(parseISO(payment.payment_date),'MMM dd,yyyy HH:mm')}
</p>
<p className="text-xs text-green-600">By: {payment.recorded_by_name}</p>
{payment.notes && (
<p className="text-xs text-green-600 mt-1">{payment.notes}</p>
)}
</div>
</div>
</div>
))}
</div>
</div>
)}

{selectedInvoice.status==='cancelled' && (
<div className="bg-red-50 p-4 rounded-lg">
<p className="text-sm font-medium text-red-800">Cancellation Reason:</p>
<p className="text-sm text-red-700">{selectedInvoice.cancellation_reason}</p>
{selectedInvoice.cancelled_by_name && (
<p className="text-xs text-red-600 mt-1">
Cancelled by: {selectedInvoice.cancelled_by_name}
</p>
)}
</div>
)}
</div>

<div className="flex justify-end pt-4">
<button
onClick={()=> setShowInvoiceModal(false)}
className="px-4 py-2 bg-grey-600 text-white rounded-lg hover:bg-grey-700 transition-colors"
>
Close
</button>
</div>
</motion.div>
</div>
)}

{/* Case Sheet Modal */}
{showCaseSheetModal && selectedAppointment && (
<CaseSheetPrint
appointment={selectedAppointment}
onClose={()=> setShowCaseSheetModal(false)}
/>
)}
</div>
);
};

export default Appointments;